// MIDAS V3 — Per-thread model picker (MODEL-PICKER Feature 1, spec §5)
//
// Compact trigger (current model name, mono, chevron) + anchored menu listing
// the 10 allowlist models (v1.1 §14.1 + v1.2 §15.2 + MODEL-PICKER-OSS +
// MODEL-VERSION-CURRENCY-1 2026-09-03: 4 Anthropic + 4 OpenAI + 2 OSS +
// MODEL-PICKER-OSS-48G: 96GB/48GB GPU-size A/B pair) grouped by provider —
// ANTHROPIC first (default prod lives there), then OPENAI, then OSS — with
// one-line descriptions and a check on the current selection. The OPENAI and OSS groups each carry a muted data-egress
// caption (§15.3: picking a gpt-* model sends the turn to OpenAI; picking
// the oss-* model sends it to a RunPod-hosted endpoint running our own open
// model — both third parties distinct from Anthropic).
// Design source: design_handoff_multichat_v33/
// model-picker.jsx (trigger + menu redlines) — recreated with the V3 token
// system and the RailRowMenuV3 portal pattern (portal to document.body,
// re-wrapped in `.midas-v3-root p-{theme}` so V3 custom properties resolve;
// the shadcn/Radix v2 menus portal OUT of the token scope — see
// RailRowMenuV3's header for the root cause).
//
// Behaviour (design handoff):
//   • Opens UPWARD (the composer is pinned to the viewport bottom),
//     right-aligned to the trigger, 8px offset. 120ms fade + 4px rise.
//   • Click toggles. Click-outside and Esc close; focus returns to trigger.
//   • Keyboard: ↑↓ move, ↵ select, Esc close.
//   • Scope is PER-THREAD (spec §5): selection lives in
//     midasV3.modelOverrideByThread keyed by `threadKey`
//     (activeThreadId ?? "draft" for the primary; col.threadId per column).
//     Every send resolves the model AT DISPATCH TIME in the sendMessage
//     thunk — switching mid-thread affects subsequent messages only.
//   • `.cc` ONLY (isCcDevSurface). Renders null off-`.cc` — the composer is
//     pixel-identical to today on `.com` (no reserved space, no hidden
//     markup). UX-only gate; the bot's server-side Origin allowlist is the
//     real boundary.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  DEFAULT_MODEL_ID,
  ModelOverrideId,
} from "../../../../shared/chatBot/models/chatBotModels";
import { setThreadModel } from "../../midasV3Slice";
import { isCcDevSurface } from "./ccSurface";

export interface ModelPickerV3Props {
  /** Per-thread selection key: activeThreadId ?? "draft" (primary) or the
   *  attached column's threadId. */
  threadKey: string;
  disabled?: boolean;
}

const MENU_WIDTH = 268;
const MENU_OFFSET_Y = 8;
const VIEWPORT_MARGIN = 8;

/** Shared eyebrow style — the "MODEL · THIS THREAD" pattern. The provider
 *  group sub-headers (ANTHROPIC / OPENAI, §15.2) use EXACTLY this style:
 *  JetBrains Mono, 10px, uppercase, faint, non-interactive. */
const EYEBROW_STYLE: React.CSSProperties = {
  padding: "5px 12px 6px",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--midas-v3-faint)",
};

const resolveTheme = (): "p-claude" | "p-machineid" => {
  if (typeof document === "undefined") return "p-claude";
  return document.querySelector(".midas-v3-root.p-claude") !== null
    ? "p-claude"
    : "p-machineid";
};

const ChevronDown: React.FC = () => (
  <svg
    viewBox="0 0 10 6"
    width="9"
    height="6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden="true"
  >
    <path d="M1 1.2l4 3.6 4-3.6" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    viewBox="0 0 16 16"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 8.5l3.2 3 6.3-7" />
  </svg>
);

const ModelPickerV3: React.FC<ModelPickerV3Props> = ({
  threadKey,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const selected: ModelOverrideId =
    useAppSelector((s) => s.midasV3.modelOverrideByThread[threadKey]) ??
    DEFAULT_MODEL_ID;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const [position, setPosition] = useState<{
    bottom: number;
    right: number;
    /** v1.1 (§14.1): available height ABOVE the trigger. The menu opens
     *  upward from the composer; at 6 items (~370px) a short viewport could
     *  clip it at the top — clamp + internal scroll instead. */
    maxHeight: number;
  } | null>(null);

  // Display metadata for the 10 models. Ids come from the closed union ONLY;
  // names are product names (untranslated values in all locales),
  // descriptions translate (spec §9 + §14.1).
  const models = useMemo(
    () => [
      // MODEL-VERSION-CURRENCY-1 (2026-09-03, bot ticket of the same name):
      // the ANTHROPIC group is 4 ids in the bot's own menu order -- Sonnet 5
      // (the new DEFAULT), Sonnet 4.6 (retained, labelled "previous
      // default"), Opus 5 (new; PLAIN only, the bot lists no thinking
      // variant for it), Haiku 4.5. The three removed ids (Opus 4.8,
      // Opus 4.8 thinking, Sonnet 5 thinking) are DELETED, not hidden: the
      // bot exact-matches and silently degrades a stale id to the default,
      // so a dead option would look like it worked while serving Sonnet 5.
      // The "(default)" marker lives in the description text -- same
      // mechanism the picker has always used to mark the default entry.
      {
        id: "claude-sonnet-5" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.sonnet5.name", {
          defaultValue: "Sonnet 5",
        }) as string,
        desc: translate("midas.v3.modelPicker.sonnet5.desc", {
          defaultValue: "Current production model (default)",
        }) as string,
      },
      {
        id: "claude-sonnet-4-6" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.sonnet46.name", {
          defaultValue: "Sonnet 4.6",
        }) as string,
        desc: translate("midas.v3.modelPicker.sonnet46.desc", {
          defaultValue: "Previous default model",
        }) as string,
      },
      {
        id: "claude-opus-5" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.opus5.name", {
          defaultValue: "Opus 5",
        }) as string,
        desc: translate("midas.v3.modelPicker.opus5.desc", {
          defaultValue: "Most capable model",
        }) as string,
      },
      {
        id: "claude-haiku-4-5" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.haiku45.name", {
          defaultValue: "Haiku 4.5",
        }) as string,
        desc: translate("midas.v3.modelPicker.haiku45.desc", {
          defaultValue: "Fastest, lightweight (no thinking variant)",
        }) as string,
      },
      // v1.2 (§15.1, bot `e0edad7`): four OpenAI allowlist ids. Order per
      // spec: GPT-5 mini, GPT-5, GPT-5 (thinking), GPT-4o. These render
      // under the OPENAI group eyebrow with the egress caption (§15.3).
      {
        id: "gpt-5-mini" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.gpt5mini.name", {
          defaultValue: "GPT-5 mini",
        }) as string,
        desc: translate("midas.v3.modelPicker.gpt5mini.desc", {
          defaultValue: "Fast, lightweight (OpenAI)",
        }) as string,
      },
      {
        id: "gpt-5" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.gpt5.name", {
          defaultValue: "GPT-5",
        }) as string,
        desc: translate("midas.v3.modelPicker.gpt5.desc", {
          defaultValue: "OpenAI flagship",
        }) as string,
      },
      {
        id: "gpt-5-thinking" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.gpt5thinking.name", {
          defaultValue: "GPT-5 (thinking)",
        }) as string,
        desc: translate("midas.v3.modelPicker.gpt5thinking.desc", {
          defaultValue: "OpenAI flagship, extended reasoning — slower",
        }) as string,
      },
      {
        id: "gpt-4o" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.gpt4o.name", {
          defaultValue: "GPT-4o",
        }) as string,
        desc: translate("midas.v3.modelPicker.gpt4o.desc", {
          defaultValue: "Previous-generation OpenAI",
        }) as string,
      },
      // MODEL-PICKER-OSS (bot `dc9367f`): third-provider allowlist id. Our
      // own open model (Qwen3.6-27B-FP8 on vLLM) on a RunPod-hosted endpoint.
      // Renders under the OSS group eyebrow with its own egress caption.
      // Stats-only pick (no thinking); the pod is parked when idle so a pick
      // may surface a single sanitized error instead of an answer.
      // MODEL-PICKER-OSS-48G: relabeled "(OSS · 96GB)" so the pair below
      // reads as a GPU-size A/B (bot-offered relabel, our call — the wire
      // value `oss-qwen3.6-27b` is UNCHANGED, label/desc only).
      {
        id: "oss-qwen3.6-27b" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.ossQwen3627b.name", {
          defaultValue: "Qwen3.6-27B (OSS · 96GB)",
        }) as string,
        desc: translate("midas.v3.modelPicker.ossQwen3627b.desc", {
          defaultValue: "Our open model, self-hosted on RunPod (96GB GPU pod)",
        }) as string,
      },
      // MODEL-PICKER-OSS-48G: the SAME open model on a smaller 48GB-GPU pod,
      // A/B against the 96GB entry above. Identical behavior/rendering:
      // stats-only, `backend_served: "oss"`, parked-pod single-error
      // tolerance, shared OSS group eyebrow + egress caption. A pick before
      // the bot side + pod are live is silently ignored (default model
      // answers).
      {
        id: "oss-qwen3.6-27b-48g" as ModelOverrideId,
        name: translate("midas.v3.modelPicker.ossQwen3627b48g.name", {
          defaultValue: "Qwen3.6-27B (OSS · 48GB)",
        }) as string,
        desc: translate("midas.v3.modelPicker.ossQwen3627b48g.desc", {
          defaultValue: "Same open model on a smaller 48GB GPU pod",
        }) as string,
      },
    ],
    [translate]
  );

  // Provider grouping (§15.2 + MODEL-PICKER-OSS): Anthropic first, then
  // OpenAI, then OSS. Slices of the FLAT models array — keyboard nav indexes
  // the flat array only, so arrows cross the group boundaries seamlessly and
  // never land on an eyebrow/caption.
  // MODEL-VERSION-CURRENCY-1: 6 -> 4 Anthropic ids. These counts slice the
  // FLAT models array above and MUST match MODEL_OVERRIDE_IDS' order in
  // chatBotModels.ts (4 Anthropic, 4 OpenAI, 2 OSS).
  const ANTHROPIC_COUNT = 4;
  const OPENAI_COUNT = 4;
  const groups = useMemo(
    () => [
      {
        key: "anthropic",
        label: translate("midas.v3.modelPicker.groupAnthropic", {
          defaultValue: "Anthropic",
        }) as string,
        startIndex: 0,
        items: models.slice(0, ANTHROPIC_COUNT),
        egress: null as string | null,
      },
      {
        key: "openai",
        label: translate("midas.v3.modelPicker.groupOpenai", {
          defaultValue: "OpenAI",
        }) as string,
        startIndex: ANTHROPIC_COUNT,
        items: models.slice(ANTHROPIC_COUNT, ANTHROPIC_COUNT + OPENAI_COUNT),
        // Data-egress disclosure (§15.3) — compact, non-alarmist, no icon,
        // no warning color. Renders under the OPENAI eyebrow, above items.
        egress: translate("midas.v3.modelPicker.openaiEgress", {
          defaultValue: "Sent to OpenAI for processing",
        }) as string,
      },
      {
        key: "oss",
        label: translate("midas.v3.modelPicker.groupOss", {
          defaultValue: "OSS",
        }) as string,
        startIndex: ANTHROPIC_COUNT + OPENAI_COUNT,
        items: models.slice(ANTHROPIC_COUNT + OPENAI_COUNT),
        // MODEL-PICKER-OSS data-egress disclosure — same tone/placement as
        // the OpenAI caption: the turn goes to a RunPod-hosted server (a
        // 3rd-party HOST running our own open model, distinct from Anthropic
        // AND OpenAI).
        egress: translate("midas.v3.modelPicker.ossEgress", {
          defaultValue: "Sent to a RunPod-hosted server running our open model",
        }) as string,
      },
    ],
    [models, translate]
  );

  const current = models.find((m) => m.id === selected) ?? models[0];

  const tooltip = translate("midas.v3.modelPicker.tooltip", {
    defaultValue:
      "Model for this thread — every message in this conversation uses it",
  }) as string;
  const openAria = translate("midas.v3.modelPicker.openAria", {
    defaultValue: "Choose model",
  }) as string;
  const eyebrow = translate("midas.v3.modelPicker.eyebrow", {
    defaultValue: "Model · this thread",
  }) as string;

  // ── Anchor the menu above the trigger, right-aligned, clamped. ──
  const recomputePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const tr = trig.getBoundingClientRect();
    let right = window.innerWidth - tr.right;
    // Left-edge clamp so the 268px menu never leaves the viewport.
    if (window.innerWidth - right - MENU_WIDTH < VIEWPORT_MARGIN) {
      right = Math.max(
        VIEWPORT_MARGIN,
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
      );
    }
    const bottom = window.innerHeight - tr.top + MENU_OFFSET_Y;
    // Space remaining above the trigger (menu grows upward). Floor of 160px
    // keeps at least ~3 rows usable on pathologically short viewports.
    const maxHeight = Math.max(
      160,
      window.innerHeight - bottom - VIEWPORT_MARGIN
    );
    setPosition({ bottom, right, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    recomputePosition();
    const onWinChange = () => recomputePosition();
    window.addEventListener("resize", onWinChange);
    window.addEventListener("scroll", onWinChange, true);
    return () => {
      window.removeEventListener("resize", onWinChange);
      window.removeEventListener("scroll", onWinChange, true);
    };
  }, [open, recomputePosition]);

  // ── Open/close side effects: Esc, outside-click, focus. ──
  useEffect(() => {
    if (!open) return undefined;
    setActiveIndex(Math.max(0, models.findIndex((m) => m.id === selected)));
    const id = window.setTimeout(() => menuRef.current?.focus(), 0);
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus returns to the trigger on close.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  const handleSelect = useCallback(
    (model: ModelOverrideId) => {
      dispatch(setThreadModel({ threadKey, model }));
      setOpen(false);
    },
    [dispatch, threadKey]
  );

  // Keyboard nav (§15.2): arrows move through the 10 model ITEMS only —
  // eyebrows and the egress captions are plain divs (non-focusable, skipped
  // by construction since activeIndex indexes the flat models array);
  // navigation crosses the Anthropic→OpenAI→OSS boundaries seamlessly.
  // Home/End jump to first/last item. Esc unchanged.
  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(models.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (e.key === "Home") {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setActiveIndex(models.length - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const m = models[activeIndex];
        if (m) handleSelect(m.id);
      }
    },
    [models, activeIndex, handleSelect]
  );

  // With internal scroll at 10 items (§15.2) the highlighted row can sit
  // outside the scrolled viewport — keep it visible during keyboard nav.
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    if (!open) return;
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // `.cc`-only surface (spec §5): render NOTHING off-`.cc` — no reserved
  // space, composer pixel-identical to today. All hooks above run
  // unconditionally so the hook order is stable across environments.
  if (!isCcDevSurface()) return null;

  const active = hovered || open;

  const menuNode =
    open && typeof document !== "undefined" ? (
      <div
        className={`midas-v3-root ${resolveTheme()}`}
        // COMPOSER-MENU: marks this portal as a NESTED submenu of the ☰
        // composer popover. ComposerMenuV3's outside-mousedown + Esc handlers
        // check `closest('[data-midas-v3-submenu]')` so interacting with this
        // menu (portaled to document.body, outside the popover DOM) doesn't
        // close the hosting popover. Purely a DOM marker — no behavior change
        // inside this component.
        data-midas-v3-submenu=""
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1400,
          background: "transparent",
          pointerEvents: "none",
        }}
        role="presentation"
      >
        <div
          ref={menuRef}
          role="menu"
          aria-label={openAria}
          tabIndex={-1}
          onKeyDown={handleMenuKeyDown}
          className="midas-v33-menu-enter"
          style={{
            position: "fixed",
            bottom: position?.bottom ?? -9999,
            right: position?.right ?? -9999,
            width: MENU_WIDTH,
            // v1.2 (§15.2): cap at min(60vh, content), keep the v1.1
            // above-trigger viewport clamp. 10 items + 2 eyebrows + the
            // egress caption exceed the clamp near the composer on short
            // viewports — internal scroll is REQUIRED now (supersedes
            // v1.1's "only if needed"). V3 scrollbar tokens apply via the
            // `.midas-v3-root` portal wrapper (midas-v3-tokens.scss).
            maxHeight: position
              ? `min(60vh, ${position.maxHeight}px)`
              : undefined,
            overflowY: "auto",
            background: "var(--midas-v3-card)",
            color: "var(--midas-v3-ink)",
            border: "1px solid var(--midas-v3-border)",
            borderRadius: 8,
            padding: 6,
            boxShadow:
              "0 8px 24px rgba(15, 23, 30, 0.10), 0 2px 6px rgba(15, 23, 30, 0.06)",
            fontFamily: "var(--font-sans), Sora, system-ui, sans-serif",
            pointerEvents: "auto",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Eyebrow: MODEL · THIS THREAD */}
          <div style={EYEBROW_STYLE}>{eyebrow}</div>
          {/* v1.2 (§15.2): provider groups — Anthropic first, then OpenAI.
              Eyebrows + egress caption are non-interactive divs; only the
              model items participate in focus/keyboard nav. */}
          {groups.map((g) => (
            <div
              key={g.key}
              role="group"
              aria-label={g.label}
              style={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <div style={EYEBROW_STYLE} aria-hidden="true">
                {g.label}
              </div>
              {g.egress && (
                <div
                  style={{
                    padding: "0 12px 5px",
                    fontSize: 11,
                    lineHeight: 1.35,
                    color: "var(--midas-v3-muted)",
                  }}
                >
                  {g.egress}
                </div>
              )}
              {g.items.map((m, localIdx) => {
                const idx = g.startIndex + localIdx;
                return (
                  <ModelMenuItem
                    key={m.id}
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    name={m.name}
                    desc={m.desc}
                    selected={selected === m.id}
                    highlighted={activeIndex === idx}
                    onHighlight={() => setActiveIndex(idx)}
                    onSelect={() => handleSelect(m.id)}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={openAria}
        title={tooltip}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          height: 30,
          padding: "0 8px",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          flexShrink: 0,
          border: "none",
          borderRadius: 7,
          background: active ? "var(--accent-soft)" : "transparent",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "background 0.12s ease, color 0.12s ease",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11.5,
            letterSpacing: 0,
            color: active ? "var(--accent)" : "var(--muted)",
            transition: "color 0.12s ease",
            whiteSpace: "nowrap",
          }}
        >
          {current.name}
        </span>
        <span
          style={{
            color: active ? "var(--accent)" : "var(--faint)",
            display: "inline-flex",
            transition: "color 0.12s ease",
          }}
        >
          <ChevronDown />
        </span>
      </button>
      {menuNode ? ReactDOM.createPortal(menuNode, document.body) : null}
    </div>
  );
};

// ── Menu item — name (mono 12.5/500) + desc (Sora 11.5) + 16px check slot ──
// forwardRef (v1.2 §15.2): the parent scrolls the highlighted item into view
// during keyboard nav now that the 10-item menu scrolls internally.
const ModelMenuItem = React.forwardRef<
  HTMLButtonElement,
  {
    name: string;
    desc: string;
    selected: boolean;
    highlighted: boolean;
    onHighlight: () => void;
    onSelect: () => void;
  }
>(({ name, desc, selected, highlighted, onHighlight, onSelect }, ref) => (
  <button
    ref={ref}
    type="button"
    role="menuitemradio"
    aria-checked={selected}
    onClick={onSelect}
    onMouseEnter={onHighlight}
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 9,
      width: "100%",
      padding: "8px 12px",
      borderRadius: 6,
      border: "none",
      background: highlighted ? "var(--midas-v3-accent-soft)" : "transparent",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "inherit",
      transition: "background-color 0.12s ease",
    }}
  >
    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: "block",
          color: "var(--midas-v3-ink)",
          fontFamily: "var(--font-mono)",
          fontSize: 12.5,
          fontWeight: 500,
          letterSpacing: 0,
        }}
      >
        {name}
      </span>
      <span
        style={{
          display: "block",
          fontSize: 11.5,
          color: "var(--midas-v3-muted)",
          marginTop: 2,
          lineHeight: 1.35,
        }}
      >
        {desc}
      </span>
    </span>
    <span
      style={{
        width: 16,
        flexShrink: 0,
        color: "var(--midas-v3-accent)",
        marginTop: 1,
        display: "inline-flex",
      }}
    >
      {selected && <CheckIcon />}
    </span>
  </button>
));
ModelMenuItem.displayName = "ModelMenuItem";

export default ModelPickerV3;
